import {EditHistoryDialogQuery} from '@github-ui/server-persisted-queries'
import type {EditHistoryDialogQueryQuery} from '@github-ui/server-persisted-queries/types/EditHistoryDialogQuery'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

type DialogNode = NonNullable<EditHistoryDialogQueryQuery['node']>
// The `node` union is the resolved UserContentEdit shape plus one `{id}` variant per
// other node type (an id that resolves to a different type). The resolved edit always
// carries `editedAt`, so Extract narrows to just that member.
export type EditHistoryNode = Extract<DialogNode, {editedAt: string}>

export function editHistoryDialogQueryKey(userContentEditId: string) {
  return ['markdown-edit-history-viewer', 'edit-history-dialog', userContentEditId] as const
}

// A resolved UserContentEdit always carries `editedAt`; the other `node` union variants
// are `{id}` objects (an id that resolved to a different node type). Narrow via `editedAt`.
function isEditHistoryNode(node: DialogNode | null | undefined): node is EditHistoryNode {
  return !!node && 'editedAt' in node
}

export async function fetchEditHistoryDialog(
  userContentEditId: string,
  signal?: AbortSignal,
): Promise<EditHistoryNode> {
  const response = await verifiedFetchJSON(
    `/issues/edit-history-dialog?id=${encodeURIComponent(userContentEditId)}&queryId=${EditHistoryDialogQuery}`,
    {
      signal,
    },
  )
  if (!response.ok) throw new Error(`Edit history dialog fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: EditHistoryDialogQueryQuery; errors?: unknown[]}
  if (json.errors?.length || !json.data) throw new Error('Edit history dialog query returned errors')

  const node = json.data.node
  if (!isEditHistoryNode(node)) throw new Error('Edit history dialog query returned no edit')

  return node
}
